import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, Upload, Download, CheckCircle, XCircle, FileSpreadsheet, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const AdminImport = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  
  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/import/template`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to download template');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla_importacion_bka.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Plantilla descargada');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Error al descargar plantilla');
    }
  };
  
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('El archivo debe ser Excel (.xlsx o .xls)');
        return;
      }
      setSelectedFile(file);
      setResults(null);
    }
  };
  
  const handleImport = async () => {
    if (!selectedFile) {
      toast.error('Selecciona un archivo');
      return;
    }
    
    setImporting(true);
    setResults(null);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/import/students`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error en importación');
      }
      
      setResults(data);
      
      if (data.imported > 0) {
        toast.success(`${data.imported} alumnos importados exitosamente`);
      }
      if (data.failed > 0) {
        toast.error(`${data.failed} filas con errores`);
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.message || 'Error al importar');
    } finally {
      setImporting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-black page-transition">
      {/* Header */}
      <div className="bg-zinc-950 border-b border-zinc-800 p-4">
        <div className="max-w-4xl mx-auto flex items-center space-x-4">
          <Button onClick={() => navigate('/admin/students')} variant="ghost" size="sm" data-testid="back-button">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-heading text-white uppercase">IMPORTAR ALUMNOS</h1>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-4xl mx-auto p-4 space-y-6 pb-20">
        {/* Instructions */}
        <Card className="bg-zinc-950 border-zinc-800 p-6">
          <h2 className="text-lg font-heading text-white uppercase mb-4">INSTRUCCIONES</h2>
          <ol className="space-y-3 text-zinc-400 text-sm">
            <li className="flex items-start space-x-2">
              <span className="bg-primary text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
              <span>Descarga la plantilla Excel con el formato correcto</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="bg-primary text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
              <span>Completa los datos de los alumnos (máximo 100 por archivo)</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="bg-primary text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
              <span>Sube el archivo y revisa los resultados</span>
            </li>
          </ol>
          
          <Button
            onClick={handleDownloadTemplate}
            data-testid="download-template-button"
            className="mt-4 bg-zinc-800 hover:bg-zinc-700 text-white"
          >
            <Download className="mr-2 h-4 w-4" />
            DESCARGAR PLANTILLA
          </Button>
        </Card>
        
        {/* Upload Area */}
        <Card className="bg-zinc-950 border-zinc-800 p-6">
          <div 
            className={`border-2 border-dashed rounded-sm p-8 text-center transition-colors cursor-pointer ${
              selectedFile ? 'border-primary bg-primary/5' : 'border-zinc-700 hover:border-zinc-500'
            }`}
            onClick={() => fileInputRef.current?.click()}
          >
            {selectedFile ? (
              <div className="space-y-2">
                <FileSpreadsheet className="h-12 w-12 text-primary mx-auto" />
                <p className="text-white font-medium">{selectedFile.name}</p>
                <p className="text-xs text-zinc-500">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="h-12 w-12 text-zinc-600 mx-auto" />
                <p className="text-zinc-400">Click para seleccionar archivo Excel</p>
                <p className="text-xs text-zinc-600">.xlsx o .xls</p>
              </div>
            )}
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          {selectedFile && (
            <div className="mt-4 flex space-x-3">
              <Button
                onClick={handleImport}
                disabled={importing}
                data-testid="import-button"
                className="flex-1 bg-primary hover:bg-[#b20710] text-white rounded-sm font-bold uppercase"
              >
                {importing ? 'IMPORTANDO...' : 'IMPORTAR ALUMNOS'}
              </Button>
              <Button
                onClick={() => {
                  setSelectedFile(null);
                  setResults(null);
                }}
                variant="outline"
              >
                Cancelar
              </Button>
            </div>
          )}
        </Card>
        
        {/* Results */}
        {results && (
          <Card className="bg-zinc-950 border-zinc-800 p-6" data-testid="import-results">
            <h2 className="text-lg font-heading text-white uppercase mb-4">RESULTADOS</h2>
            
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center p-3 bg-zinc-900 rounded-sm">
                <p className="text-2xl font-bold text-white">{results.total_rows}</p>
                <p className="text-xs text-zinc-500">Total Filas</p>
              </div>
              <div className="text-center p-3 bg-green-500/10 rounded-sm border border-green-500/30">
                <p className="text-2xl font-bold text-green-500">{results.imported}</p>
                <p className="text-xs text-zinc-500">Importados</p>
              </div>
              <div className="text-center p-3 bg-red-500/10 rounded-sm border border-red-500/30">
                <p className="text-2xl font-bold text-red-500">{results.failed}</p>
                <p className="text-xs text-zinc-500">Con Errores</p>
              </div>
            </div>
            
            {/* Success list */}
            {results.success.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-heading text-green-500 uppercase mb-2 flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4" />
                  <span>Importados Exitosamente ({results.success.length})</span>
                </h3>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {results.success.map((item) => (
                    <div key={item.student_id} className="text-sm text-zinc-400 flex items-center space-x-2">
                      <CheckCircle className="h-3 w-3 text-green-500" />
                      <span>Fila {item.row}: {item.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Errors list */}
            {results.errors.length > 0 && (
              <div>
                <h3 className="text-sm font-heading text-red-500 uppercase mb-2 flex items-center space-x-2">
                  <XCircle className="h-4 w-4" />
                  <span>Errores ({results.errors.length})</span>
                </h3>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {results.errors.map((item) => (
                    <div key={`row-${item.row}`} className="p-2 bg-red-500/10 rounded-sm border border-red-500/30">
                      <p className="text-sm text-white flex items-center space-x-2">
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                        <span>Fila {item.row}: {item.name}</span>
                      </p>
                      <ul className="mt-1 ml-6 text-xs text-red-400">
                        {item.errors.map((err) => (
                          <li key={`row-${item.row}-${err}`}>• {err}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminImport;
